/**
 * Selling Plans / Recharges - Moon Moon Agency
 * Gère la sélection achat unique / abonnement sur la fiche produit.
 *
 * Logique :
 * - Les variants = quantités (1 bouteille, 6 bouteilles, 12 bouteilles)
 * - Achat unique : cartes de quantité + stepper pour le variant "1 bouteille"
 * - Abonnement : select quantité (variants avec selling plans) + select fréquence
 *   (fréquences dépendent du variant sélectionné)
 */

class RechargesSelector extends HTMLElement {
	constructor() {
		super();
		this.variants = [];
		this.sellingPlanGroups = [];
		this.quantityOptionIndex = 0; // index 0-based dans variant.options[]
		this.stepperQty = 1;          // quantité courante du stepper
		this.activeUnitVariantId = null;
		this.form = null;
	}

	connectedCallback() {
		this.form = this.closest('form');
		if (!this.form) return;

		this.parseData();
		this.bindEvents();
		this.initState();
	}

	/* ─────────────────────────────────────────────
	 * INIT
	 * ───────────────────────────────────────────── */

	parseData() {
		const jsonEl = this.querySelector('[data-recharges-json]');
		if (!jsonEl) return;
		try {
			const data = JSON.parse(jsonEl.textContent);
			this.variants = data.variants || [];
			this.sellingPlanGroups = data.selling_plan_groups || [];
			this.stockMap = data.stock || {};
			this.contenanceMap = data.contenance || {};
			this.unitTitleMap = data.unit_title || {};
			// quantity_option_position et millesime_option_position sont 1-based
			// variant.options[] est 0-based
			this.quantityOptionIndex = (data.quantity_option_position || 1) - 1;
			this.millesimeOptionIndex = (data.millesime_option_position || 0) - 1;
			this.isComposition = data.composition === true;
		} catch (e) {
			console.error('[Recharges] Erreur parsing data', e);
		}
	}

	/* ─────────────────────────────────────────────
	 * MILLESIME / SELLING PLANS VISIBILITY
	 * ───────────────────────────────────────────── */

	millesimeHasSellingPlans(variantId) {
		const variant = this.variants.find(v => v.id == variantId);
		if (!variant || this.millesimeOptionIndex < 0) return true;
		const millesimeValue = variant.options[this.millesimeOptionIndex];
		return this.variants.some(v =>
			v.options[this.millesimeOptionIndex] === millesimeValue &&
			v.selling_plan_allocations?.length > 0
		);
	}

	updateForMillesime(variantId) {
		const selectedVariant = this.variants.find(v => v.id == variantId);
		if (!selectedVariant || this.millesimeOptionIndex < 0) return;

		const millesimeValue = selectedVariant.options[this.millesimeOptionIndex];
		const hasPlans = this.millesimeHasSellingPlans(variantId);

		// 1. Update Subscription Panel visibility
		const subPanel = this.querySelector('[data-panel="subscription"]');
		if (subPanel) {
			subPanel.style.display = hasPlans ? '' : 'none';
			// If we are in subscription mode but it's now hidden, switch to one-time
			if (!hasPlans && this.dataset.mode === 'subscription') {
				this.setMode('one-time');
			}
		}

		// 2. Filter / Update Achat Unique cards — disable transition to avoid height-grow glitch
		const oneTimeOuter = this.querySelector('[data-panel="one-time"] .mm-recharges-panel-content-outer');
		if (oneTimeOuter && oneTimeOuter.style.maxHeight !== '0px' && oneTimeOuter.style.maxHeight !== '0') {
			oneTimeOuter.style.transition = 'none';
			oneTimeOuter.style.maxHeight = 'none';
		}
		this.updateAchatUniqueCards(millesimeValue);
		requestAnimationFrame(() => {
			this.refreshActivePanelHeight('one-time');
			if (oneTimeOuter) requestAnimationFrame(() => { oneTimeOuter.style.transition = ''; });
		});

		// 3. Update Subscription Qty Select
		this.populateSubscriptionQtySelect(millesimeValue);

		// 4. Update Header Price
		this.updateOneTimeHeaderPrice();
		this.updateSubscriptionHeaderPrice();
	}

	updateAchatUniqueCards(millesimeValue) {
		const container = this.querySelector('[data-qty-cards-container]');
		if (!container) return;

		const visibleVariants = this.variants.filter(v => v.options[this.millesimeOptionIndex] === millesimeValue);
		
		// Map variant IDs to elements
		const cardMap = {};
		this.querySelectorAll('[data-qty-card]').forEach(card => {
			cardMap[card.dataset.variantId] = card;
			card.style.display = 'none'; // hide all first
		});

		let firstAvailable = null;
		visibleVariants.forEach(v => {
			const card = cardMap[v.id];
			if (card) {
				card.style.display = '';
				if (v.available && !firstAvailable) firstAvailable = card;
			}
		});

		// Ensure one card is active if current one is hidden
		const activeCard = this.querySelector('[data-qty-card].mm-recharges-qty-card--active');
		if (activeCard && activeCard.style.display === 'none') {
			if (firstAvailable) {
				this.selectQtyCard(firstAvailable, true);
			}
		}
	}

	initState() {
		// Initialiser l'état selon le variant sélectionné à l'arrivée
		const currentVariantId = this.form.querySelector('[data-selected-variant-id]')?.value;
		if (currentVariantId) {
			this.updateForMillesime(currentVariantId);
		}

		// Initialiser la carte active
		const activeCard = this.querySelector('[data-qty-card].mm-recharges-qty-card--active');
		if (activeCard) {
			this.selectQtyCard(activeCard, false);
		} else {
			const firstVisibleAvailable = this.querySelector('[data-qty-card]:not([style*="display: none"])[data-variant-available="true"]');
			if (firstVisibleAvailable) this.selectQtyCard(firstVisibleAvailable, false);
		}

		// Ouvrir le panel actif sans animation à l'init
		const activeMode = this.dataset.mode || 'one-time';
		this.setMode(activeMode, false);
	}

	bindEvents() {
		if (this._eventsBound) return;
		this._eventsBound = true;

		// Clic sur le header du panel → switch de mode
		this.querySelectorAll('[data-panel-trigger]').forEach(trigger => {
			trigger.addEventListener('click', () => {
				this.setMode(trigger.dataset.panelTrigger);
			});
			trigger.addEventListener('keydown', e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.setMode(trigger.dataset.panelTrigger);
				}
			});
		});

		// Cartes de quantité (achat unique)
		this.querySelectorAll('[data-qty-card]').forEach(card => {
			card.addEventListener('click', () => {
				if (card.dataset.variantAvailable !== 'true') return;
				this.selectQtyCard(card);
			});
			card.addEventListener('keydown', e => {
				if (card.dataset.variantAvailable !== 'true') return;
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					this.selectQtyCard(card);
				}
			});
		});

		// Stepper
		this.querySelectorAll('[data-stepper-decrease]').forEach(btn => {
			btn.addEventListener('click', (e) => { e.stopPropagation(); this.changeStepperQty(-1); });
		});
		this.querySelectorAll('[data-stepper-increase]').forEach(btn => {
			btn.addEventListener('click', (e) => { e.stopPropagation(); this.changeStepperQty(1); });
		});

		// Select quantité abonnement
		const qtySelect = this.querySelector('[data-subscription-qty-select]');
		if (qtySelect) {
			qtySelect.addEventListener('change', () => {
				this.updateFrequencySelect();
				this.updateSubscriptionFormInputs();
				this.updateSubscriptionHeaderPrice();
				this.updateSubscriptionUnitTitle(parseInt(qtySelect.value, 10));
				this.reloadProductElements(qtySelect.value);
			});
		}

		// Select fréquence abonnement
		const freqSelect = this.querySelector('[data-subscription-freq-select]');
		if (freqSelect) {
			freqSelect.addEventListener('change', () => {
				this.updateSubscriptionFormInputs();
				this.updateSubscriptionHeaderPrice();
			});
		}

		// Changement de variant (format/capacité produit)
		document.addEventListener('product:variant-found', (e) => {
			const variantId = e.detail?.data?.variantId;
			const sourceModal = e.target.closest?.('[data-mm-variants-modal]') || null;
			if (sourceModal !== (this.closest('[data-mm-variants-modal]') || null)) return;
			if (variantId) {
				if (this.isComposition) this.syncCompositionVariant(variantId);
				this.updateForMillesime(variantId);
				this.updateSubscriptionUnitTitle(variantId);
			}
		});

		if (this.isComposition && !this.closest('[data-mm-variants-modal]')) {
			document.addEventListener('product:product-reload-complete', () => this._refreshCompositionTotal());
		}
	}

	syncCompositionVariant(variantId) {
		const variant = this.variants.find(v => v.id == variantId);
		if (!variant) return;

		const card = this.querySelector('[data-qty-card]');
		if (card) {
			card.dataset.variantId = variant.id;
			card.dataset.variantPrice = variant.price;
			card.dataset.variantComparePrice = variant.compare_at_price || 0;
			card.dataset.variantAvailable = String(variant.available);
			card.classList.toggle('mm-recharges-qty-card--disabled', !variant.available);
			if (variant.available) card.removeAttribute('aria-disabled');
			else card.setAttribute('aria-disabled', 'true');
			card.tabIndex = variant.available ? 0 : -1;
			const priceEl = card.querySelector('[data-card-price]');
			if (priceEl) priceEl.textContent = this.formatMoney(variant.price / 100);
			this.activeUnitVariantId = String(variant.id);
		}

		const qtyOption = this.querySelector('[data-subscription-qty-select] option');
		if (qtyOption) qtyOption.value = variant.id;

		const freqSelect = this.querySelector('[data-subscription-freq-select]');
		const selectedPlanId = freqSelect?.value;
		this.updateFrequencySelect();
		if (selectedPlanId && freqSelect.querySelector(`option[value="${selectedPlanId}"]`)) {
			freqSelect.value = selectedPlanId;
		}

		this.updateOneTimeHeaderPrice();
		this.updateSubscriptionHeaderPrice();
		this._refreshCompositionTotal();
	}

	_refreshCompositionTotal() {
		const sellingPlanInput = this.form?.querySelector('[data-selling-plan-id]');
		const formIsSubscription = !!sellingPlanInput && !sellingPlanInput.disabled;
		if (formIsSubscription !== (this.dataset.mode === 'subscription')) return;

		if (formIsSubscription) this.updateSubscriptionTotalPrice();
		else this.updateTotalPrice();
	}

	refreshActivePanelHeight(panelName) {
		const panel = this.querySelector(`[data-panel="${panelName}"]`);
		if (!panel) return;
		const outer = panel.querySelector('.mm-recharges-panel-content-outer');
		if (outer && outer.style.maxHeight !== '0px' && outer.style.maxHeight !== '0') {
			outer.style.maxHeight = outer.scrollHeight + 'px';
		}
	}

	/* ─────────────────────────────────────────────
	 * MODE SWITCH
	 * ───────────────────────────────────────────── */

	setMode(mode, animate = true) {
		const isSameMode = this.dataset.mode === mode;
		if (isSameMode && animate) return;
		
		this.dataset.mode = mode;

		const oneTimeRadio = this.querySelector('[data-panel="one-time"] input[type="radio"]');
		const subRadio = this.querySelector('[data-panel="subscription"] input[type="radio"]');
		if (oneTimeRadio) oneTimeRadio.checked = mode === 'one-time';
		if (subRadio) subRadio.checked = mode === 'subscription';

		if (mode === 'one-time') {
			this.disableSellingPlan();
			this.updateOneTimeFormInputs();
			if (this.activeUnitVariantId) this.reloadProductElements(this.activeUnitVariantId);
		} else {
			this.updateFrequencySelect();
			this.updateSubscriptionFormInputs();
			this.updateSubscriptionHeaderPrice();
			const qtySelect = this.querySelector('[data-subscription-qty-select]');
			if (qtySelect?.value) {
				this.updateSubscriptionUnitTitle(parseInt(qtySelect.value, 10));
				this.reloadProductElements(qtySelect.value);
			}
		}

		// Animer l'ouverture/fermeture des panels
		this.querySelectorAll('[data-panel]').forEach(panel => {
			const outer = panel.querySelector('.mm-recharges-panel-content-outer');
			const header = panel.querySelector('[data-panel-trigger]');
			const isActive = panel.dataset.panel === mode;
			
			if (outer) {
				if (!animate) {
					outer.style.transition = 'none';
					outer.style.maxHeight = isActive ? 'none' : '0';
					if (isActive) {
						// On remet scrollHeight après un court délai pour que les futures animations marchent
						setTimeout(() => {
							outer.style.maxHeight = outer.scrollHeight + 'px';
						}, 50);
					}
					outer.style.transition = '';
				} else {
					outer.style.maxHeight = isActive ? outer.scrollHeight + 'px' : '0';
				}
			}
			if (header) header.setAttribute('aria-expanded', isActive ? 'true' : 'false');
		});
	}

	/* ─────────────────────────────────────────────
	 * ACHAT UNIQUE
	 * ───────────────────────────────────────────── */

	selectQtyCard(card, updateForm = true) {
		// Reset le prix de toutes les cartes
		this.querySelectorAll('[data-qty-card]').forEach(c => {
			c.classList.remove('mm-recharges-qty-card--active');
			c.setAttribute('aria-pressed', 'false');
			const priceEl = c.querySelector('[data-card-price]');
			const originalPrice = parseInt(c.dataset.variantPrice, 10);
			if (priceEl && !isNaN(originalPrice)) {
				priceEl.textContent = this.formatMoney(originalPrice / 100);
			}
		});

		card.classList.add('mm-recharges-qty-card--active');
		card.setAttribute('aria-pressed', 'true');
		this.activeUnitVariantId = card.dataset.variantId;

		// Reset stepper à 1
		this.stepperQty = 1;
		this.updateStepperDisplay();

		if (updateForm) {
			this.updateOneTimeFormInputs();
			this.updateOneTimeHeaderPrice();
			this.updateVariantMedia(card.dataset.variantId);
			this.reloadProductElements(card.dataset.variantId);
		} else {
			this.setVariantId(card.dataset.variantId);
		}
	}

	updateVariantMedia(variantId) {
		const variant = this.variants.find(v => v.id === parseInt(variantId, 10));
		if (variant && window.MediaManager?.updateImagesVariant) {
			window.MediaManager.updateImagesVariant(variant);
		}
	}

	async reloadProductElements(variantId) {
		const baseUrl = this.dataset.url || window.location.pathname;
		const sectionId = this.dataset.section;
		if (!sectionId || this.closest('[data-mm-variants-modal]')) return;

		const skipIds = ['prices', 'price', 'recharges'];
		const hasTargets = [...document.querySelectorAll('[data-product-reload]')].some(
			el => el.dataset.reloadId && !skipIds.includes(el.dataset.reloadId)
		);
		if (!hasTargets) return;

		const url = `${baseUrl}?variant=${variantId}&section_id=${sectionId}`;
		try {
			const response = await fetch(url);
			if (!response.ok) return;
			const html = new DOMParser().parseFromString(await response.text(), 'text/html');

			let mediaUpdated = false;
			document.querySelectorAll('[data-product-reload]').forEach(el => {
				const reloadId = el.dataset.reloadId;
				if (!reloadId || skipIds.includes(reloadId)) return;
				const newEl = html.querySelector(`[data-reload-id="${reloadId}"]`);
				if (newEl && el.innerHTML.trim() !== newEl.innerHTML.trim()) {
					el.innerHTML = newEl.innerHTML;
					if (reloadId === 'product-medias') mediaUpdated = true;
				}
			});
			if (mediaUpdated) {
				window.ProductMediaSliders?.destroy('main');
				window.ProductMediaSliders?.init('main', false);
			}
		} catch (e) { /* silently fail */ }
	}

	changeStepperQty(delta) {
		this.stepperQty = Math.max(1, this.stepperQty + delta);
		this.updateStepperDisplay();

		this.setFormQuantity(String(this.stepperQty));
		this.updateTotalPrice();
	}

	updateStepperDisplay() {
		const activeCard = this.querySelector('[data-qty-card].mm-recharges-qty-card--active');
		const el = activeCard?.querySelector('[data-stepper-display]');
		if (el) el.textContent = `x${this.stepperQty}`;
	}

	updateActiveCardPrice(card) {
		const priceEl = card.querySelector('[data-card-price]');
		if (!priceEl) return;
		const variantPrice = parseInt(card.dataset.variantPrice, 10);
		if (isNaN(variantPrice)) return;
		priceEl.textContent = this.formatMoney((variantPrice * this.stepperQty) / 100);
	}

	updateOneTimeFormInputs() {
		if (!this.activeUnitVariantId) return;
		this.setVariantId(this.activeUnitVariantId);

		this.setFormQuantity(String(this.stepperQty));
		this.disableSellingPlan();
		this.updateTotalPrice();
	}

	updateOneTimeHeaderPrice() {
		const el = this.querySelector('[data-one-time-price-value]');
		if (!el) return;
		const activeCard = this.querySelector('[data-qty-card].mm-recharges-qty-card--active');
		if (!activeCard) return;
		const unitPrice = parseInt(activeCard.dataset.variantPrice, 10);
		if (!isNaN(unitPrice)) {
			el.textContent = this.formatMoney(unitPrice / 100);
		}
	}

	updateTotalPrice() {
		const scope = this.closest('[data-mm-variants-modal]') || this.closest('form') || document;
		const wrapper = scope.querySelector('.mm-product-prices-wrapper');
		if (!wrapper) return;
		const activeCard = this.querySelector('[data-qty-card].mm-recharges-qty-card--active');
		if (!activeCard) return;
		const unitPrice = parseInt(activeCard.dataset.variantPrice, 10);
		if (isNaN(unitPrice)) return;
		const comparePrice = parseInt(activeCard.dataset.variantComparePrice, 10) || 0;
		const total = (unitPrice * this.stepperQty) / 100;

		let compareEl = wrapper.querySelector('.mm-product-prices-compare');
		if (comparePrice > unitPrice) {
			const compareTotal = (comparePrice * this.stepperQty) / 100;
			if (!compareEl) {
				compareEl = document.createElement('p');
				compareEl.className = 'mm-product-prices-compare mm-text mm-linethrough mm-black-500';
				wrapper.prepend(compareEl);
			}
			compareEl.textContent = this.formatMoney(compareTotal);
		} else if (compareEl) {
			compareEl.remove();
		}

		const priceEl = wrapper.querySelector('.mm-product-prices-price');
		if (priceEl) priceEl.textContent = this.formatMoney(total);
	}

	/* ─────────────────────────────────────────────
	 * ABONNEMENT
	 * ───────────────────────────────────────────── */

	populateSubscriptionQtySelect(millesimeValue) {
		const select = this.querySelector('[data-subscription-qty-select]');
		if (!select) return;

		select.innerHTML = '';

		let firstAvailableValue = null;
		
		// Filter variants by millesime if present
		const filteredVariants = this.millesimeOptionIndex >= 0 
			? this.variants.filter(v => v.options[this.millesimeOptionIndex] === millesimeValue)
			: this.variants;

		filteredVariants.forEach(variant => {
			if (!variant.selling_plan_allocations?.length) return;
			const qtyLabel = variant.options[this.quantityOptionIndex] || variant.title;
			const contenance = this.contenanceMap[variant.id];
			const fullLabel = contenance ? `${qtyLabel} (${contenance})` : qtyLabel;
			const option = document.createElement('option');
			option.value = variant.id;
			option.textContent = variant.available ? fullLabel : `${fullLabel} — Indisponible`;
			if (!variant.available) option.disabled = true;
			else if (!firstAvailableValue) firstAvailableValue = variant.id.toString();
			select.appendChild(option);
		});

		if (firstAvailableValue) select.value = firstAvailableValue;

		this.updateFrequencySelect();
	}

	updateFrequencySelect() {
		const qtySelect = this.querySelector('[data-subscription-qty-select]');
		const freqSelect = this.querySelector('[data-subscription-freq-select]');
		if (!qtySelect || !freqSelect) return;

		const selectedVariantId = parseInt(qtySelect.value, 10);
		const variant = this.variants.find(v => v.id === selectedVariantId);

		freqSelect.innerHTML = '';
		if (!variant?.selling_plan_allocations?.length) return;

		const planMap = this.buildSellingPlanMap();

		variant.selling_plan_allocations.forEach(allocation => {
			const planId = allocation.selling_plan_id;
			const plan = planMap[planId];
			if (!plan) return;

			const option = document.createElement('option');
			option.value = planId;
			option.textContent = this.formatFrequencyLabel(plan);
			option.dataset.planPrice = allocation.price;
			freqSelect.appendChild(option);
		});
	}

	buildSellingPlanMap() {
		const map = {};
		this.sellingPlanGroups.forEach(group => {
			(group.selling_plans || []).forEach(plan => {
				map[plan.id] = plan;
			});
		});
		return map;
	}

	updateSubscriptionFormInputs() {
		const qtySelect = this.querySelector('[data-subscription-qty-select]');
		const freqSelect = this.querySelector('[data-subscription-freq-select]');
		if (!qtySelect || !freqSelect) return;

		this.setVariantId(qtySelect.value);
		this.enableSellingPlan(freqSelect.value);
		this.setFormQuantity('1');
		this.updateSubscriptionTotalPrice();
	}

	updateSubscriptionTotalPrice() {
		const scope = this.closest('[data-mm-variants-modal]') || this.closest('form') || document;
		const wrapper = scope.querySelector('.mm-product-prices-wrapper');
		if (!wrapper) return;

		const freqSelect = this.querySelector('[data-subscription-freq-select]');
		const qtySelect = this.querySelector('[data-subscription-qty-select]');
		if (!freqSelect || !qtySelect) return;

		let subPrice = 0;
		let originalPrice = 0;

		const selectedOpt = freqSelect.options[freqSelect.selectedIndex];
		const selectedVariantId = parseInt(qtySelect.value, 10);
		const variant = this.variants.find(v => v.id === selectedVariantId);

		if (selectedOpt?.dataset.planPrice) {
			subPrice = parseInt(selectedOpt.dataset.planPrice, 10);
		}

		if (variant) originalPrice = variant.price;

		let compareEl = wrapper.querySelector('.mm-product-prices-compare');
		if (originalPrice > subPrice) {
			if (!compareEl) {
				compareEl = document.createElement('p');
				compareEl.className = 'mm-product-prices-compare mm-text mm-linethrough mm-black-500';
				wrapper.prepend(compareEl);
			}
			compareEl.textContent = this.formatMoney(originalPrice / 100);
		} else if (compareEl) {
			compareEl.remove();
		}

		const priceEl = wrapper.querySelector('.mm-product-prices-price');
		if (priceEl) priceEl.textContent = this.formatMoney(subPrice / 100);
	}

	updateSubscriptionHeaderPrice() {
		const priceEl = this.querySelector('[data-subscription-price-value]');
		if (!priceEl) return;

		const freqSelect = this.querySelector('[data-subscription-freq-select]');
		if (!freqSelect) return;

		const selectedOpt = freqSelect.options[freqSelect.selectedIndex];
		if (selectedOpt?.dataset.planPrice) {
			const qtySelect = this.querySelector('[data-subscription-qty-select]');
			const variantId = qtySelect ? parseInt(qtySelect.value, 10) : null;
			const contenance = variantId ? this.contenanceMap[variantId] : null;
			const multiplier = this.isComposition ? 1 : this._getMultiplierFromContenance(contenance);
			priceEl.textContent = this.formatMoney(parseInt(selectedOpt.dataset.planPrice, 10) / 100 / multiplier);
			return;
		}
	}

	_getMultiplierFromContenance(contenance) {
		if (!contenance) return 1;
		const match = contenance.toLowerCase().match(/^(\d+)x/);
		const n = match ? parseInt(match[1], 10) : 1;
		return n > 1 ? n : 1;
	}

	updateSubscriptionUnitTitle(variantId) {
		const el = this.querySelector('[data-subscription-unit-title]');
		if (!el) return;
		const title = this.unitTitleMap[variantId];
		if (title) el.textContent = title;
	}

	/* ─────────────────────────────────────────────
	 * FORM HELPERS
	 * ───────────────────────────────────────────── */

	setVariantId(variantId) {
		const input = this.form.querySelector('[data-selected-variant-id]');
		if (input) input.value = variantId;
	}

	setFormQuantity(qty) {
		if (!this.form) return;
		this.form.querySelectorAll('[data-recharges-quantity]').forEach(input => {
			input.value = qty;
		});
	}

	enableSellingPlan(planId) {
		const input = this.form?.querySelector('[data-selling-plan-id]');
		if (!input) return;
		input.value = planId;
		input.removeAttribute('disabled');
	}

	disableSellingPlan() {
		const input = this.form?.querySelector('[data-selling-plan-id]');
		if (!input) return;
		input.value = '';
		input.setAttribute('disabled', '');
	}

	/* ─────────────────────────────────────────────
	 * UTILS
	 * ───────────────────────────────────────────── */

	formatFrequencyLabel(plan) {
		const sources = [];
		if (plan.options?.length) sources.push(String(plan.options[0].value || ''));
		if (plan.name) sources.push(plan.name);

		for (const str of sources) {
			if (!str || /^\d+$/.test(str)) continue;
			const match = str.match(/^(\d+\s+(?:day|week|month|year)s?)/i);
			if (match) return match[1];
			if (str.split(' ').length <= 3) return str;
		}
		return plan.name || String(plan.id);
	}

	formatMoney(amount) {
		return new Intl.NumberFormat('fr-FR', {
			style: 'currency',
			currency: 'EUR',
		}).format(amount).replace(/\s/g, '').replace('.', ',');
	}
}

if (!customElements.get('mm-product-recharges')) {
    customElements.define('mm-product-recharges', RechargesSelector);
}
